import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoins,
  faLock,
  faChevronRight,
  faCopy,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import CoinHistoryModal from "./CoinHistoryModal";

/**
 * Owner tools block for the private profile (/profile).
 * Reuses existing data/actions:
 *   - QUSHi Coins  -> profile.coins
 *   - QUSHi Bank   -> onQushiBank (existing resetPortfolio modal)
 *   - Referral     -> real referral link (same as ReferAFriend), shown in the
 *                     new card design with the /doublecircleicon.png logo.
 */
const OwnerToolsBlock = ({ coins = 0, referralCode, userName, onQushiBank, coinsGuideId = "profile-guide-coins" }) => {
  const [copied, setCopied] = useState(false);
  const [showCoinHistory, setShowCoinHistory] = useState(false);

  // Real, working referral link (same one ReferAFriend copies).
  const referralLink = `https://app.qushi.io/auth/register/${referralCode || ""}`;
  // Friendly label shown in the card.
  const inviteLabel = `QUSHi.io/invite/${userName || referralCode || ""}`;

  const handleCopy = () => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-4 flex flex-col gap-4">
      {/* QUSHi Coins — tap to open Coin History / How to Earn */}
      <div
        id={coinsGuideId}
        role="button"
        tabIndex={0}
        onClick={() => setShowCoinHistory(true)}
        className="w-full border rounded-xl border-[#F3F4F6] p-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <img
            src="/qushi-coins-icon-com.png"
            alt="QUSHi Coins"
            className="h-10 w-10 object-contain shrink-0"
          />
          <div className="min-w-0">
            <p className="font-bold text-base font-poppins text-[#6B7280] flex items-center justify-between gap-2 mb-1">
              QUSHi Coins{" "}
              <span className="text-primaryColor ml-auto text-xl">
                {Number(coins || 0).toLocaleString("en-US")}
              </span>
            </p>
            <p className="text-[10px] text-secondary leading-snug">
              Earn them by trading, learning, and competing.
            </p>
          </div>
        </div>
        <FontAwesomeIcon icon={faChevronRight} className="text-[#C9CCD1] shrink-0" />
      </div>

<div className="w-full border rounded-xl border-[#F3F4F6] p-4 ">
      {/* Owner tools header */}
      <p className="flex items-center gap-2 text-[15px] font-bold font-poppins text-[#1A1A1A] mb-3">
        Owner Tools
        <img src="/lock-icon.svg" alt="lock-icon" />
      </p>

      {/* QUSHi Bank */}
      <div className="border-y border-[#F9FAFB] py-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-sm font-poppins text-[#1A1A1A] mb-1">The Comeback</p>
          <p className="text-[11px] text-secondary leading-snug">
            Fresh $1M. New record. New start date.
          </p>
        </div>
        <div className="flex flex-col items-end shrink-0">
          {/* NOTE: price is static per the design; wire to real fee if needed */}
          <span className="text-[10px] font-semibold text-[#9CA3AF] mb-1">$4.99</span>
          <button
            type="button"
            onClick={() => onQushiBank && onQushiBank()}
            className="px-4 py-2 text-[11px] rounded-lg border border-primaryColor text-primaryColor bg-[#004C50]/5 font-semibold whitespace-nowrap"
          >
            Learn more
          </button>
        </div>
      </div>

      {/* Referral link */}
      <div className="pt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <img
            src="/doublecircleicon.png"
            alt="referral"
            className="w-9 h-9 object-contain shrink-0"
          />
          <div className="min-w-0">
            <p className="font-bold text-sm font-poppins text-[#1A1A1A]">Referral link</p>
            <p className="text-[10px] text-secondary truncate">{inviteLabel}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          title="Copy referral link"
          className="shrink-0 w-9 h-9 flex items-center justify-center text-[#8A8B8F] hover:text-[#173E3B]"
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        </button>
      </div>
      </div>

      <CoinHistoryModal
        open={showCoinHistory}
        onClose={() => setShowCoinHistory(false)}
      />
    </div>
  );
};

export default OwnerToolsBlock;
